import { AdminGroupDraft, FormList } from 'Interfaces/Admin';

export interface PayloadContext {
  /** The group as it is stored, or undefined when adding a new one. */
  existing?: { id: number; parentId: number | null };
  /** True once the admin has actually touched the parent field. */
  parentTouched: boolean;
  /** The group the signed-in admin belongs to. */
  accountGroupId: number | null;
  /** True when that group is itself an organization. */
  accountIsOrganization: boolean;
  formList?: FormList;
  htmlZip: string | null;
}

/**
 *  Builds the group payload, carrying two rules that are easy to lose:
 *
 *  BG-2840 — leaving the parent field empty must not detach an existing group
 *  from its parent. Only an explicit edit may change it.
 *
 *  A group created by an organization admin defaults to sitting under that
 *  organization rather than at the root of the account.
 */
export const buildGroupPayload = (
  draft: AdminGroupDraft, ctx: PayloadContext,
): AdminGroupDraft => {
  const { existing, parentTouched, accountGroupId, accountIsOrganization } = ctx;

  let parentId = draft.parentId ?? null;
  if (existing && !parentTouched && !parentId) {
    parentId = existing.parentId ?? null;
  }
  if (!parentId && accountIsOrganization && (!existing || existing.id !== accountGroupId)) {
    parentId = accountGroupId;
  }

  return {
    ...draft,
    parentId,
    added_by: accountGroupId,
    // A chosen form list always supplies its own name.
    formListName: ctx.formList ? ctx.formList.name : draft.formListName,
    htmlZip: ctx.htmlZip || draft.htmlZip,
  };
};

/** Blocking checks the group form runs before saving. Returns a message key. */
export const validateGroup = (draft: AdminGroupDraft): string | null => {
  if (!draft.name) {
    return 'groupNameRequired';
  }
  if (draft.organization && !draft.parentId) {
    return 'organizationNeedsParent';
  }
  return null;
};

export interface GroupFormVisibility {
  /** The organization flag only means anything inside a superuser account. */
  organization: boolean;
  /** A superuser group has no parent to choose. */
  parent: boolean;
  /** A superuser group may not be deactivated. */
  activeLocked: boolean;
}

/**
 *  Which parts of the group form are shown, from GroupView.render. The superuser
 *  checkbox itself is never rendered: it is hidden on a new group (BG-3267) and
 *  hidden once one exists, so a superuser group can never be reassigned.
 */
export const groupFormVisibility = (
  isSuperUserGroup: boolean,
  account: { superUserGroup: boolean; hasSuperUserGroup: boolean },
): GroupFormVisibility => ({
  organization: !isSuperUserGroup && (account.superUserGroup || account.hasSuperUserGroup),
  parent: !isSuperUserGroup,
  activeLocked: isSuperUserGroup,
});
